import { produce } from 'immer';
import { AdjustmentType } from '@gtintel/platform-types';
import * as scopes from '../state/scopes';
import * as tree from './tree';
export const mutations = {
  init: ({
    newState
  }) => _ => newState,
  update: ({
    changes
  }) => state => {
    return {
      ...state,
      project: {
        ...state.project,
        ...changes
      }
    };
  },
  updateFlags: ({
    changes
  }) => state => {
    return {
      ...state,
      project: {
        ...state.project,
        flags: {
          ...state.project.flags,
          ...changes
        }
      }
    };
  },
  importFromTemplate: ({
    importResult
  }) => state => produce(state, draft => {
    const scopes = Object.keys(importResult.new_scopes.index);
    draft.project = {
      ...draft.project,
      ...importResult.project_changes,
      scopes
    };
    draft.scope.index = importResult.new_scopes.index;
  }),
  addDocument: ({
    document
  }) => state => {
    return {
      ...state,
      project: {
        ...state.project,
        documents: [...state.project.documents, document]
      }
    };
  },
  removeDocument: ({
    documentId
  }) => state => {
    return {
      ...state,
      project: {
        ...state.project,
        documents: state.project.documents.filter(d => d.id !== documentId)
      }
    };
  },
  incrementAdjustmentsCost: (cost, type) => state => {
    let adjustment;
    switch (type) {
      case AdjustmentType.client_price:
        adjustment = {
          client_adjustments_cost: state.project.client_adjustments_cost + cost
        };
        break;
      case AdjustmentType.partner_price:
        adjustment = {
          partner_adjustments_cost: state.project.partner_adjustments_cost + cost
        };
        break;
      default:
        console.log('Adjustment changed but not updating client or partner costs');
        break;
    }
    return {
      ...state,
      project: {
        ...state.project,
        ...adjustment
      }
    };
  },
  subject: {
    add: subject => state => {
      return produce(state, draft => {
        draft.project.subjects.push(subject);
      });
    },
    delete: ({
      id
    }) => state => {
      const truncated = state.project.subjects.filter(s => s.id !== id);
      return {
        ...state,
        project: {
          ...state.project,
          subjects: truncated
        }
      };
    },
    edit: ({
      id,
      changes
    }) => state => {
      const existing = state.project.subjects.find(s => s.id === id);
      if (!existing) {
        return state;
      }
      const changed = {
        ...existing,
        ...changes
      };
      const subjects = state.project.subjects.map(s => s.id === id ? changed : s);
      return {
        ...state,
        project: {
          ...state.project,
          subjects
        }
      };
    },
    reorder: ({
      subjects
    }) => state => {
      return {
        ...state,
        project: {
          ...state.project,
          subjects
        }
      };
    },
    addAttachment: ({
      id,
      attachment
    }) => state => {
      const existing = state.project.subjects.find(s => s.id === id);
      if (!existing) {
        return state;
      }
      existing.attachments.push(attachment);
      const subjects = state.project.subjects.map(s => s.id === id ? existing : s);
      return {
        ...state,
        project: {
          ...state.project,
          subjects
        }
      };
    },
    removeAttachment: ({
      id,
      attachmentId
    }) => state => {
      const existing = state.project.subjects.find(s => s.id === id);
      if (!existing) {
        return state;
      }
      existing.attachments = existing.attachments.filter(attachment => attachment.id !== attachmentId);
      const subjects = state.project.subjects.map(s => s.id === id ? existing : s);
      return {
        ...state,
        project: {
          ...state.project,
          subjects
        }
      };
    }
  },
  scope: {
    add: ({
      scope
    }) => state => {
      const project = {
        ...state.project
      };
      if (state.project.scopes && state.project.scopes.length > 0) {
        project['scopes'] = [...state.project.scopes, scope.id];
      } else {
        project['scopes'] = [scope.id];
      }
      return {
        ...state,
        scope: scopes.mutations.add({
          scope
        })(state.scope),
        project
      };
    },
    clear: () => state => {
      return {
        ...state,
        scope: {
          index: {},
          relates_to: state.scope.relates_to,
          applicability: state.scope.applicability,
          service_types: state.scope.service_types
        }
      };
    },
    delete: ({
      scopeId
    }) => state => {
      const id = scopeId;
      const project = {
        ...state.project,
        scopes: state.project.scopes.filter(s => s !== id)
      };
      return {
        ...state,
        scope: scopes.mutations.delete({
          scopeId
        })(state.scope),
        project
      };
    },
    reorder: ({
      projectId,
      scopeIds
    }) => state => {
      let project = state.project;
      if (projectId) {
        project = {
          ...state.project,
          scopes: scopeIds
        };
      }
      return {
        ...state,
        project
      };
    },
    edit: scopes.mutations.edit
  },
  np_requests: {
    add: ({
      request
    }) => state => {
      return {
        ...state,
        np_requests: {
          ...state.np_requests,
          index: {
            ...state.np_requests.index,
            [request.id]: request
          }
        }
      };
    },
    open: id => state => {
      return {
        ...state,
        np_requests: {
          ...state.np_requests,
          openId: id
        }
      };
    },
    close: () => state => {
      return {
        ...state,
        np_requests: {
          ...state.np_requests,
          openId: null
        }
      };
    },
    setPartner: partner => state => {
      const id = state.np_requests.openId;
      return {
        ...state,
        np_requests: {
          ...state.np_requests,
          index: {
            ...state.np_requests.index,
            [id]: {
              ...state.np_requests.index[id],
              partner
            }
          }
        }
      };
    },
    setFee: fee => state => {
      const id = state.np_requests.openId;
      return {
        ...state,
        np_requests: {
          ...state.np_requests,
          index: {
            ...state.np_requests.index,
            [id]: {
              ...state.np_requests.index[id],
              fee
            }
          }
        }
      };
    },
    setDueDate: due_date => state => {
      const id = state.np_requests.openId;
      return {
        ...state,
        np_requests: {
          ...state.np_requests,
          index: {
            ...state.np_requests.index,
            [id]: {
              ...state.np_requests.index[id],
              due_date
            }
          }
        }
      };
    },
    changeSelection: (change, isSelected) => state => {
      const id = state.np_requests.openId;
      if (!id) return;
      const oldSelection = state.np_requests.index[id].components;
      const oldSelectionMap = tree.selectionToPaths(oldSelection);
      const changeMap = tree.selectionToPaths(change);
      const newSelectionMap = {
        ...oldSelectionMap,
        ...changeMap
      };
      if (!isSelected) {
        for (const k in changeMap) {
          delete newSelectionMap[k];
        }
      }
      const selection = Object.values(newSelectionMap);
      return {
        ...state,
        np_requests: {
          ...state.np_requests,
          index: {
            ...state.np_requests.index,
            [id]: {
              ...state.np_requests.index[id],
              components: selection,
              proposed_partners: []
            }
          }
        }
      };
    },
    send: () => state => {
      const id = state.np_requests.openId;
      return {
        ...state,
        np_requests: {
          ...state.np_requests,
          index: {
            ...state.np_requests.index,
            [id]: {
              ...state.np_requests.index[id],
              status: 1
            }
          }
        }
      };
    },
    remove: id => state => {
      const truncated = {
        ...state.np_requests.index
      };
      delete truncated[id];
      return {
        ...state,
        np_requests: {
          ...state.np_requests,
          index: {
            ...truncated
          }
        }
      };
    },
    edit: (id, changes) => state => {
      return {
        ...state,
        np_requests: {
          ...state.np_requests,
          index: {
            ...state.np_requests.index,
            [id]: {
              ...state.np_requests.index[id],
              ...changes
            }
          }
        }
      };
    }
  },
  documents: {
    build: ({
      changes
    }) => state => {
      return {
        ...state,
        document_tree: changes
      };
    }
  },
  report: {
    updateFragment: (id, body) => state => {
      const n = {
        ...state,
        project: {
          ...state.project,
          report: {
            ...state.project.report,
            fragments: {
              ...state.project.report.fragments,
              index: {
                ...state.project.report.fragments.index,
                [id]: {
                  ...state.project.report.fragments.index[id],
                  unsafe_body: body
                }
              }
            }
          }
        }
      };
      return n;
    }
  }
};
export const queries = {
  current: {
    project: () => state => state.project,
    scopes: () => state => {
      return state.project.scopes // TODO: this should always be present
      ? state.project.scopes.map(s => state.scope.index[s]) : [];
    },
    jurisdictionsFor: id => state => {
      const lookup = state.jurisdictions.reduce((memo, n) => {
        memo[n.id] = n;
        return memo;
      }, {});
      return state.project.subjects.find(s => String(s.id) === id).jurisdictions.map(i => lookup[i]);
    }
  },
  report: {
    tree: () => state => {
      const project = queries.current.project()(state);
      const sq = {
        project: () => project,
        subjects: () => project.subjects,
        scopeComponents: () => queries.current.scopes()(state),
        jurisdictionsFor: id => queries.current.jurisdictionsFor(id)(state),
        fragments: () => Object.values(state.project.report.fragments.index)
      };
      const t = tree.requestReportTreeFrom(sq);
      return t;
    }
  },
  np_requests: {
    reportTree: readonly => state => {
      const id = state.np_requests.openId;
      const sq = {
        selection: () => state.np_requests.index[id].components
      };
      const t = queries.report.tree()(state);
      tree.applySelectionState(sq, t, readonly);
      return t;
    },
    isEmptySelection: () => state => {
      const id = state.np_requests.openId;
      return state.np_requests.index[id].components.length === 0;
    },
    proposedPartners: () => state => {
      return state.np_requests.index[state.np_requests.openId].proposed_partners;
    },
    partner: () => state => {
      return state.np_requests.index[state.np_requests.openId].partner;
    },
    isSent: () => state => state.np_requests.index[state.np_requests.openId].status === 1,
    status: () => state => state.np_requests.index[state.np_requests.openId].status,
    fee: () => state => state.np_requests.index[state.np_requests.openId].fee,
    dueDate: () => state => state.np_requests.index[state.np_requests.openId].due_date,
    currency: () => state => state.project.currency,
    acceptedDate: () => state => state.np_requests.index[state.np_requests.openId].accepted_date,
    activatedDate: () => state => state.np_requests.index[state.np_requests.openId].assigned_date,
    submittedDate: () => state => state.np_requests.index[state.np_requests.openId].submitted_date,
    finishedDate: () => state => state.np_requests.index[state.np_requests.openId].finished_date,
    declinedDate: () => state => state.np_requests.index[state.np_requests.openId].declined_date,
    cancelledDate: () => state => state.np_requests.index[state.np_requests.openId].cancelled_date,
    isAssignedSubject: subjectId => state => {
      return !!Object.values(state.np_requests.index).find(request => {
        return request.components.find(component => {
          return component.subject_id == subjectId;
        });
      });
    },
    isAssignedSubjectJurisdiction: (subjectId, id) => state => {
      return !!Object.values(state.np_requests.index).find(request => {
        return request.components.find(component => {
          return component.jurisdiction_id == id && component.subject_id == subjectId;
        });
      });
    },
    isAssignedScopeComponent: id => state => {
      return !!Object.values(state.np_requests.index).find(request => {
        return request.components.find(component => {
          return component.scope_component_id == id;
        });
      });
    },
    isLeadPartnerAssignment: () => state => {
      return state.np_requests.index[state.np_requests.openId].is_lead_partner_assignment;
    }
  }
};
export const initialState = () => ({
  template: {
    index: {}
  },
  scope: scopes.initialState(),
  project: null,
  subjectTypes: [],
  jurisdictions: [],
  cx_leads: {
    index: []
  },
  quality_assurers: {
    index: []
  },
  currencies: [[]],
  preferred_currency: 'GBP',
  discount_percentage: 0,
  discount_amount: 0,
  net_price: 0,
  np_requests: {
    index: {},
    openId: null
  },
  document_tree: []
});